import { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  X, User, Clock, Tags as TagsIcon, StickyNote, MessageSquare, Ban,
  ShieldCheck, Save, Plus, Loader2, MailX, MailCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton, Separator } from '@/components/ui/misc';
import { apiFetch } from '@/lib/api';
import { formatPhone } from '@/lib/phone';
import { useT } from '@/lib/i18n';
import {
  type Contact, type ContactAddress, type HistoryItem, type Taxon,
  contactIdent, statusVariant,
} from '@/lib/contacts';

function relTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Perfil (editável via PATCH) ──────────────────────────────────────────────
function ProfileTab({ contact, onSaved }: { contact: Contact; onSaved: () => void }) {
  const t = useT();
  const [name, setName] = useState(contact.name ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [document, setDocument] = useState(contact.document ?? '');
  const [addr, setAddr] = useState<ContactAddress>(contact.address ?? {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(contact.name ?? '');
    setEmail(contact.email ?? '');
    setDocument(contact.document ?? '');
    setAddr(contact.address ?? {});
  }, [contact.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (k: keyof ContactAddress) => (v: string) => setAddr((a) => ({ ...a, [k]: v }));

  const save = async () => {
    setSaving(true);
    const r = await apiFetch('PATCH', `/contacts/${contact.id}`, {
      name: name.trim() || undefined,
      email: email.trim().toLowerCase() || undefined, // e-mail sempre lowercase
      document: document.trim() || undefined,
      document_type: contact.document_type,
      address: addr,
    });
    setSaving(false);
    if (!r.ok) { toast.error(t('contacts.saveFailed')); return; }
    toast.success(t('contacts.saved'));
    onSaved();
  };

  const field = (label: string, value: string, on: (v: string) => void, type = 'text') => (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={(e) => on(e.target.value)} />
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {field(t('contacts.name'), name, setName)}
        {field(t('contacts.email'), email, setEmail, 'email')}
        {field(t('contacts.document'), document, setDocument)}
        <div className="flex flex-col gap-1.5">
          <Label>{t('contacts.phone')}</Label>
          <Input value={contact.phone ? formatPhone(contact.phone) : t('contacts.phoneUnknown')} disabled />
        </div>
        {/* O LID do WhatsApp: o endereço de quem foi importado só com ele, ou o que o
            WhatsApp associou ao telefone. É o mesmo valor que o envio aceita em `to`. */}
        <div className="flex flex-col gap-1.5">
          <Label>{t('contacts.lid')}</Label>
          <Input value={contact.lid ?? t('contacts.lidUnknown')} disabled className="font-mono text-xs" />
        </div>
        {/* BSUID da Meta: só existe no trilho oficial, para quem escondeu o telefone
            atrás de um nome de usuário. Sem ele, o campo nem aparece. */}
        {contact.bsuid && (
          <div className="flex flex-col gap-1.5">
            <Label>{t('contacts.bsuid')}</Label>
            <Input value={contact.bsuid} disabled className="font-mono text-xs" />
          </div>
        )}
      </div>

      <Separator className="my-1" />
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('contacts.address')}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {field(t('contacts.street'), addr.street ?? '', setField('street'))}
        {field(t('contacts.number'), addr.number ?? '', setField('number'))}
        {field(t('contacts.complement'), addr.complement ?? '', setField('complement'))}
        {field(t('contacts.district'), addr.district ?? '', setField('district'))}
        {field(t('contacts.city'), addr.city ?? '', setField('city'))}
        {field(t('contacts.state'), addr.state ?? '', setField('state'))}
        {field(t('contacts.zip'), addr.zip ?? '', setField('zip'))}
        {field(t('contacts.country'), addr.country ?? '', setField('country'))}
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

// ── Timeline (mensagens + eventos) ──────────────────────────────────────────
function TimelineTab({ contactId }: { contactId: string }) {
  const t = useT();
  const { data, isLoading } = useQuery({
    queryKey: ['contact-history', contactId],
    queryFn: async (): Promise<HistoryItem[]> =>
      ((await apiFetch('GET', `/contacts/${contactId}/history`)).data as { data?: HistoryItem[] })?.data ?? [],
  });
  const items = data ?? [];

  if (isLoading) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>;
  if (items.length === 0) return <p className="py-10 text-center text-sm text-muted-foreground">{t('contacts.timelineEmpty')}</p>;

  return (
    <ol className="relative flex flex-col gap-4 border-l border-border pl-5">
      {items.map((it, i) => {
        const isMsg = it.kind === 'message';
        const body = it.payload?.body || it.payload?.caption || it.payload?.note || it.type;
        return (
          <li key={i} className="relative">
            <span className={'absolute -left-[1.4rem] top-1 size-2.5 rounded-full ring-4 ring-card ' + (isMsg ? (it.direction === 'out' ? 'bg-primary' : 'bg-emerald-500') : 'bg-muted-foreground/50')} />
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant={isMsg ? 'default' : 'secondary'} className="px-1.5 py-0 text-[10px]">
                {isMsg ? (it.direction === 'out' ? t('contacts.sent') : t('contacts.received')) : it.type.replace(/_/g, ' ')}
              </Badge>
              <span>{relTime(it.created_at)}</span>
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm">{String(body)}</p>
          </li>
        );
      })}
    </ol>
  );
}

// ── Tags & Grupos ────────────────────────────────────────────────────────────
function ChipEditor({
  title, assigned, options, onToggle,
}: {
  title: string;
  assigned: string[];
  options: Taxon[];
  onToggle: (key: string, add: boolean) => void;
}) {
  const has = (k: string) => assigned.includes(k);
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <div className="flex flex-wrap gap-2">
        {options.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
        {options.map((o) => {
          const on = has(o.key);
          return (
            <button
              key={o.id}
              onClick={() => onToggle(o.key, !on)}
              className={'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ' +
                (on ? 'border-primary bg-primary/12 text-primary' : 'border-input text-muted-foreground hover:bg-accent')}
              style={on && o.color ? { borderColor: o.color, color: o.color, background: `${o.color}1f` } : undefined}
            >
              {on ? <span>✓</span> : <Plus className="size-3" />}
              {o.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TagsGroupsTab({ contact, onChanged }: { contact: Contact; onChanged: () => void }) {
  const t = useT();
  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: async (): Promise<Taxon[]> => ((await apiFetch('GET', '/tags')).data as { data?: Taxon[] })?.data ?? [],
  });
  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: async (): Promise<Taxon[]> => ((await apiFetch('GET', '/contact-groups')).data as { data?: Taxon[] })?.data ?? [],
  });

  const toggle = (endpoint: 'tags' | 'groups') => async (key: string, add: boolean) => {
    const body = add ? { add: [key], remove: [] } : { add: [], remove: [key] };
    const r = await apiFetch('POST', `/contacts/${contact.id}/${endpoint}`, body);
    if (!r.ok) { toast.error(t('contacts.saveFailed')); return; }
    onChanged();
  };

  return (
    <div className="flex flex-col gap-6">
      <ChipEditor title={t('contacts.tags')} assigned={contact.tags ?? []} options={tags} onToggle={toggle('tags')} />
      <Separator />
      <ChipEditor title={t('contacts.groups')} assigned={contact.groups ?? []} options={groups} onToggle={toggle('groups')} />
    </div>
  );
}

// ── Notas ────────────────────────────────────────────────────────────────────
function NotesTab({ contactId, onAdded }: { contactId: string; onAdded: () => void }) {
  const t = useT();
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['contact-history', contactId],
    queryFn: async (): Promise<HistoryItem[]> =>
      ((await apiFetch('GET', `/contacts/${contactId}/history`)).data as { data?: HistoryItem[] })?.data ?? [],
  });
  const notes = (data ?? []).filter((it) => it.kind === 'event' && it.type === 'note_added');

  const add = async () => {
    if (!body.trim()) return;
    setSaving(true);
    const r = await apiFetch('POST', `/contacts/${contactId}/notes`, { body: body.trim() });
    setSaving(false);
    if (!r.ok) { toast.error(t('contacts.saveFailed')); return; }
    setBody('');
    toast.success(t('contacts.noteAdded'));
    void refetch();
    onAdded();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder={t('contacts.notePlaceholder')}
          className="w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={add} disabled={saving || !body.trim()}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            {t('contacts.addNote')}
          </Button>
        </div>
      </div>
      <Separator />
      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : notes.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('contacts.notesEmpty')}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {notes.map((n, i) => (
            <li key={i} className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="whitespace-pre-wrap break-words">{String(n.payload?.note ?? n.payload?.body ?? '')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{n.actor ? `${n.actor} · ` : ''}{relTime(n.created_at)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Drawer principal ─────────────────────────────────────────────────────────
export function ContactDrawer({
  contactId, open, onOpenChange, onMutated, onEditProfile,
}: {
  contactId: string | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onMutated: () => void;      // recarrega a lista
  onEditProfile?: () => void; // abre o histórico de conversa (opcional)
}) {
  const t = useT();
  const qc = useQueryClient();

  const { data: contact, isLoading, refetch } = useQuery({
    queryKey: ['contact', contactId],
    enabled: open && !!contactId,
    queryFn: async (): Promise<Contact | null> => {
      const r = await apiFetch('GET', `/contacts/${contactId}`);
      return r.ok ? (r.data as Contact) : null;
    },
  });

  const refresh = () => {
    void refetch();
    void qc.invalidateQueries({ queryKey: ['contact-history', contactId] });
    onMutated();
  };

  const action = async (path: string, okMsg: string) => {
    const r = await apiFetch('POST', `/contacts/${contactId}/${path}`);
    if (!r.ok) { toast.error(t('contacts.actionFailed')); return; }
    toast.success(okMsg);
    refresh();
  };

  const ident = contact ? contactIdent(contact, formatPhone) : '';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed right-0 top-0 z-50 flex h-full w-full flex-col overflow-hidden bg-card shadow-2xl outline-none md:w-[38rem] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
        >
          {/* Header */}
          <div className="flex items-start gap-3 border-b p-4">
            <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-sm font-semibold text-muted-foreground">
              {contact?.avatar_url
                ? <img src={contact.avatar_url} alt="" className="size-full object-cover" />
                : (contact?.name || ident || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate text-base font-semibold">
                {contact?.name || ident || t('contacts.contact')}
              </DialogPrimitive.Title>
              <p className="truncate text-xs text-muted-foreground">{ident}</p>
              {contact && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <Badge variant={statusVariant(contact.status)}>{t(`contacts.status.${contact.status}`)}</Badge>
                  {contact.status_reason && <span className="text-[11px] text-muted-foreground">{contact.status_reason}</span>}
                </div>
              )}
            </div>
            <button onClick={() => onOpenChange(false)} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent" aria-label={t('common.close')}>
              <X className="size-5" />
            </button>
          </div>

          {/* Ações rápidas */}
          {contact && (
            <div className="flex flex-wrap gap-2 border-b bg-muted/30 px-4 py-2.5">
              {onEditProfile && (
                <Button variant="outline" size="sm" onClick={onEditProfile}>
                  <MessageSquare className="size-4" /> {t('contacts.viewConversation')}
                </Button>
              )}
              {contact.opted_out_at ? (
                <Button variant="outline" size="sm" onClick={() => action('optin', t('contacts.optedIn'))}>
                  <MailCheck className="size-4" /> {t('contacts.optIn')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => action('optout', t('contacts.optedOut'))}>
                  <MailX className="size-4" /> {t('contacts.optOut')}
                </Button>
              )}
              {contact.status === 'blocked' ? (
                <Button variant="outline" size="sm" onClick={() => action('optin', t('contacts.unblocked'))}>
                  <ShieldCheck className="size-4" /> {t('contacts.unblock')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => action('suppress', t('contacts.blocked'))}>
                  <Ban className="size-4" /> {t('contacts.block')}
                </Button>
              )}
            </div>
          )}

          {/* Corpo */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading || !contact ? (
              <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : (
              <Tabs defaultValue="profile">
                <TabsList className="w-full justify-start overflow-x-auto">
                  <TabsTrigger value="profile"><User className="mr-1.5 size-4" />{t('contacts.tabProfile')}</TabsTrigger>
                  <TabsTrigger value="timeline"><Clock className="mr-1.5 size-4" />{t('contacts.tabTimeline')}</TabsTrigger>
                  <TabsTrigger value="tags"><TagsIcon className="mr-1.5 size-4" />{t('contacts.tabTags')}</TabsTrigger>
                  <TabsTrigger value="notes"><StickyNote className="mr-1.5 size-4" />{t('contacts.tabNotes')}</TabsTrigger>
                </TabsList>
                <TabsContent value="profile"><ProfileTab contact={contact} onSaved={refresh} /></TabsContent>
                <TabsContent value="timeline"><TimelineTab contactId={contact.id} /></TabsContent>
                <TabsContent value="tags"><TagsGroupsTab contact={contact} onChanged={refresh} /></TabsContent>
                <TabsContent value="notes"><NotesTab contactId={contact.id} onAdded={refresh} /></TabsContent>
              </Tabs>
            )}
          </div>

          {/* Rodapé com metadados */}
          {contact && (
            <div className="flex items-center justify-between gap-3 border-t px-4 py-2.5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><MessageSquare className="size-3.5" /> {contact.message_count} {t('contacts.messages').toLowerCase()}</span>
              <span>{t('contacts.createdAt')}: {relTime(contact.created_at)}</span>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
